import React, { useState, useEffect } from 'react';
import { Bookmark } from 'lucide-react';
import { getAuthHeaders } from '../context/AuthContext';
import { BACKEND_URL } from '../config/api';

const BookmarkButton = ({ designId, initialBookmarked = false, size = 'default', onToggle }) => {
  const [isBookmarked, setIsBookmarked] = useState(initialBookmarked);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsBookmarked(initialBookmarked);
  }, [initialBookmarked]);

  const toggleBookmark = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (isLoading) return;
    
    setIsLoading(true);
    const newState = !isBookmarked;
    setIsBookmarked(newState); // Optimistic update
    
    try {
      const method = newState ? 'POST' : 'DELETE';
      const response = await fetch(`${BACKEND_URL}/api/bookmarks/${designId}`, {
        method,
        credentials: 'include',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        // Revert on error
        setIsBookmarked(!newState);
      } else {
        onToggle?.(newState);
      }
    } catch (err) {
      console.error('Failed to toggle bookmark:', err);
      setIsBookmarked(!newState); // Revert on error
    } finally {
      setIsLoading(false);
    }
  };

  const iconSize = size === 'small' ? 16 : size === 'large' ? 24 : 20;
  const buttonClasses = size === 'small' 
    ? 'p-1.5' 
    : size === 'large' 
      ? 'p-2.5' 
      : 'p-2';

  return (
    <button
      onClick={toggleBookmark}
      disabled={isLoading}
      className={`${buttonClasses} rounded-full transition-all duration-200 ${
        isBookmarked 
          ? 'bg-sage text-white hover:bg-sage-dark' 
          : 'bg-white/80 backdrop-blur-sm text-gray-soft hover:text-sage hover:bg-white'
      } ${isLoading ? 'opacity-50' : ''}`}
      title={isBookmarked ? 'Remove bookmark' : 'Bookmark this design'}
      data-testid={`bookmark-btn-${designId}`}
    >
      <Bookmark 
        size={iconSize} 
        className={isBookmarked ? 'fill-current' : ''} 
      />
    </button>
  );
};

export default BookmarkButton;
